import React, { useState, useEffect, useCallback } from 'react';
import { getReportDetail } from '../api';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, SpinnerGap, CheckCircle, Clock, ShieldCheck,
  Download, Printer, Star, X, CaretDown, CaretUp
} from '@phosphor-icons/react';

const STATUS_STYLES = {
  'Approved': 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  'Pending Review': 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  'Draft': 'bg-slate-400/15 text-slate-600 border-slate-400/30',
  'Evidence Gap': 'bg-red-500/15 text-red-700 border-red-500/30',
};

const FW_COLORS = {
  'IFRS S2': 'bg-blue-500/15 text-blue-700 border-blue-500/30',
  'GRI': 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  'SASB': 'bg-violet-500/15 text-violet-700 border-violet-500/30',
  'TNFD': 'bg-teal-500/15 text-teal-700 border-teal-500/30',
  'CDP': 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  'CBAM': 'bg-orange-500/15 text-orange-700 border-orange-500/30',
};

export default function ReportDetailView({ reportId, onBack }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getReportDetail(reportId);
      setReport(data);
      // Auto-expand all sections
      const expanded = {};
      (data.sections || []).forEach((_, i) => { expanded[i] = true; });
      setExpandedSections(expanded);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [reportId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="report-detail-loading">
      <SpinnerGap size={32} className="text-primary animate-spin" />
    </div>
  );

  if (!report) return (
    <div className="text-center py-12">
      <p className="text-text-secondary font-body">Report not found.</p>
      <button onClick={onBack} className="mt-4 text-primary underline font-body text-sm">Back to Reports</button>
    </div>
  );

  const toggleSection = (i) => setExpandedSections(prev => ({ ...prev, [i]: !prev[i] }));
  const cs = report.compliance_status || {};

  return (
    <div className="space-y-5" data-testid="report-detail-view">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-background border border-border text-text-secondary hover:text-text-primary transition-all mt-0.5" data-testid="report-detail-back">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-body font-semibold border ${FW_COLORS[report.framework] || 'bg-slate-400/15 text-slate-600'}`}>{report.framework}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-body font-semibold border ${STATUS_STYLES[report.status] || ''}`}>{report.status}</span>
            <span className="text-[10px] font-body text-text-secondary">{report.period}</span>
          </div>
          <h1 className="font-heading text-xl md:text-2xl font-extrabold text-text-primary tracking-tight" data-testid="report-detail-title">{report.title}</h1>
          <div className="flex items-center gap-3 mt-1.5 text-[11px] font-body text-text-secondary flex-wrap">
            <span>Owner: {report.owner}</span>
            <span>Type: {report.type}</span>
            <span>Updated: {new Date(report.last_updated).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button className="p-2 rounded-lg border border-border hover:border-primary text-text-secondary hover:text-primary transition-all" title="Print"><Printer size={16} /></button>
          <button className="p-2 rounded-lg border border-border hover:border-primary text-text-secondary hover:text-primary transition-all" title="Download"><Download size={16} /></button>
        </div>
      </div>

      {/* Executive Summary */}
      {report.executive_summary && (
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="report-exec-summary">
          <h2 className="font-heading text-sm font-bold text-text-primary uppercase tracking-wider mb-3">Executive Summary</h2>
          <p className="font-body text-sm text-text-primary leading-relaxed">{report.executive_summary}</p>
        </div>
      )}

      {/* Key Metrics */}
      {report.key_metrics && report.key_metrics.length > 0 && (
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="report-key-metrics">
          <h2 className="font-heading text-sm font-bold text-text-primary uppercase tracking-wider mb-3">Key Metrics</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {report.key_metrics.map((m, i) => (
              <div key={i} className="p-3 bg-background rounded-lg border border-border/50 text-center">
                <p className="font-heading text-lg font-bold text-text-primary">{m.value}</p>
                <p className="text-[10px] font-body text-text-secondary mt-0.5">{m.metric}</p>
                {m.trend && (
                  <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-body font-semibold ${m.trend === 'down' ? 'bg-emerald-500/15 text-emerald-700' : m.trend === 'up' ? (m.change?.includes('-') ? 'bg-emerald-500/15 text-emerald-700' : 'bg-blue-500/15 text-blue-700') : 'bg-slate-400/15 text-slate-600'}`}>
                    {m.change}
                  </span>
                )}
                {m.baseline && (
                  <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-body font-semibold bg-primary/10 text-primary">Baseline</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compliance Status */}
      {cs.disclosure_completeness != null && (
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="report-compliance">
          <h2 className="font-heading text-sm font-bold text-text-primary uppercase tracking-wider mb-3">Compliance Status</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-background rounded-lg border border-border/50 text-center">
              <div className="relative w-14 h-14 mx-auto mb-1.5">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-border/40" />
                  <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray={`${cs.disclosure_completeness} ${100 - cs.disclosure_completeness}`} className={cs.disclosure_completeness >= 80 ? 'text-emerald-500' : cs.disclosure_completeness >= 60 ? 'text-amber-500' : 'text-red-500'} />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-heading font-bold text-text-primary">{cs.disclosure_completeness}%</span>
              </div>
              <p className="text-[10px] font-body text-text-secondary">Disclosure Complete</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border/50 text-center">
              <div className="relative w-14 h-14 mx-auto mb-1.5">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-border/40" />
                  <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray={`${cs.evidence_coverage} ${100 - cs.evidence_coverage}`} className={cs.evidence_coverage >= 80 ? 'text-emerald-500' : cs.evidence_coverage >= 60 ? 'text-amber-500' : 'text-red-500'} />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-heading font-bold text-text-primary">{cs.evidence_coverage}%</span>
              </div>
              <p className="text-[10px] font-body text-text-secondary">Evidence Coverage</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border/50 text-center flex flex-col items-center justify-center">
              {cs.third_party_verified ? (
                <CheckCircle size={28} weight="fill" className="text-emerald-600 mb-1" />
              ) : (
                <Clock size={28} weight="duotone" className="text-amber-500 mb-1" />
              )}
              <p className="text-[10px] font-body text-text-secondary">{cs.third_party_verified ? 'Verified' : 'Not Verified'}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border/50 text-center flex flex-col items-center justify-center">
              <ShieldCheck size={28} weight="duotone" className="text-primary mb-1" />
              <p className="text-[10px] font-body text-text-secondary leading-tight">{cs.assurance_provider || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Report Sections */}
      {report.sections && report.sections.length > 0 && (
        <div className="space-y-3" data-testid="report-sections">
          {report.sections.map((sec, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(i)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-background/30 transition-colors"
                data-testid={`report-section-${i}`}
              >
                <h3 className="font-heading text-sm font-bold text-text-primary">{sec.title}</h3>
                {expandedSections[i] ? <CaretUp size={14} className="text-tertiary" /> : <CaretDown size={14} className="text-tertiary" />}
              </button>
              {expandedSections[i] && (
                <div className="px-5 pb-5 border-t border-border/50">
                  <div className="prose prose-sm max-w-none font-body text-text-primary text-sm leading-relaxed mt-3">
                    <ReactMarkdown>{sec.content}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
